/**
 * How the pre-season pages read the fields bata-platform v0.102.0 added for them.
 *
 * ── Which cluster scheme row is the current refresh ────────────────────────────────────────
 *
 * `preseason.cluster_fit` writes every weekly refresh of a scheme as its own version, and the read
 * sorts the versions as text, so `r10` comes before `r2`. The platform states which row holds the
 * scheme's newest refresh (`isLatestRefresh`) and its ordinal (`refreshSequence`); the page reads
 * those, never the text order and never «the first unfrozen row», which was the OLDEST refresh
 * (contract audit B1, M2). A scheme the platform flags no row for has no current refresh the page
 * can name, and the page says so rather than picking one.
 *
 * ── Which curve a row is, and which curve a buy line cites ─────────────────────────────────
 *
 * A publishing run writes every size curve under its own batch id, so `versionId` names the RUN
 * and is shared by every article curve of it (B2): a curve is `curveId` with `versionId`, and a
 * buy line names its curve by `sizeCurveId` beside `sizeCurveVersionId`. `rawShares` is published
 * empty on a door curve, whose stored raw mix was the article's (B4): an empty map is read here as
 * «no raw curve», never as raw shares of nothing.
 */

import type { BuyLineSummary, ClusterSchemeSummary, SizeCurveSummary } from '@/api/generated';

/** The current refresh of each scheme, as the platform flags it; one row per scheme, sorted by scheme. Pure. */
export function currentRefreshes(schemes: readonly ClusterSchemeSummary[]): readonly ClusterSchemeSummary[] {
  return schemes
    .filter((scheme) => scheme.isLatestRefresh)
    .sort((a, b) => a.schemeId.localeCompare(b.schemeId));
}

/** The door a door curve is for; `null` on an article curve. */
export function curveStoreOf(curve: Pick<SizeCurveSummary, 'storeKey'>): string | null {
  return typeof curve.storeKey === 'string' && curve.storeKey !== '' ? curve.storeKey : null;
}

/** The curve a buy line was sized from. */
export function lineCurveIdOf(line: Pick<BuyLineSummary, 'sizeCurveId'>): string | null {
  return line.sizeCurveId === '' ? null : line.sizeCurveId;
}

/** A curve's raw shares, or `undefined` when it has none — absent, or published empty (a door curve). */
export function rawSharesOf(curve: Pick<SizeCurveSummary, 'rawShares'>): Readonly<Record<string, number>> | undefined {
  const raw = curve.rawShares;
  return raw === undefined || Object.keys(raw).length === 0 ? undefined : raw as Readonly<Record<string, number>>;
}

/** A curve's row identity: `curveId` with `versionId`, since the version alone names the run. */
export function curveRowKey(curve: Pick<SizeCurveSummary, 'curveId' | 'versionId'>): string {
  return [curve.curveId, curve.versionId].join('|');
}
